/**
 * Attack-path CSV / JSON import + export helpers.
 *
 * Mirror of step4-threats/threatCsv.ts. Export uses the read-mode
 * column labels (all 20 ISO 21434 Annex G fields). Array cell
 * `preconditions` joins with `; ` inside quoted cells on export
 * and splits back on `;` / newline on import.
 */
import {
  ATTACK_PATH_COLUMNS_READ,
  type AttackPathRecord,
} from './attackPathFieldSchema';

const EXPORT_COLUMNS = ATTACK_PATH_COLUMNS_READ;

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Trigger a CSV download with `attack_paths_YYYY-MM-DD.csv` filename. */
export function exportAttackPathsToCsv(paths: AttackPathRecord[]): void {
  const header = EXPORT_COLUMNS.map((c) => c.label).join(',');
  const rows = paths.map((p) =>
    EXPORT_COLUMNS.map((c) => escapeCsv((p as Record<string, unknown>)[c.key])).join(','),
  );
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `attack_paths_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

/** Friendly column-header reverse-map. Annex G `Time#` / `Exp#` /
 *  `Know#` / `Win#` / `Equip#` headers (the short numeric pair-mates)
 *  unslug to their long agent-field names. */
const HEADER_OVERRIDES: Record<string, string> = {
  id: 'attack_path_id',
  threat: 'threat_id',
  asset: 'asset_id',
  vector: 'attack_vector',
  agent: 'threat_agent',
  attack_path: 'attack_path',
  preconditions: 'preconditions',
  time: 'elapsed_time',
  'time#': 'elapsed_time_value',
  expertise: 'specialist_expertise',
  'exp#': 'expertise_value',
  knowledge: 'knowledge_of_item',
  'know#': 'knowledge_value',
  window: 'window_of_opportunity',
  'win#': 'window_value',
  equipment: 'equipment',
  'equip#': 'equipment_value',
  total: 'total_attack_potential',
  feasibility: 'attack_feasibility_rating',
  justification: 'justification',
};

function headerToKey(header: string): string {
  const col = EXPORT_COLUMNS.find(
    (c) => c.label.toLowerCase() === header.trim().toLowerCase(),
  );
  if (col) return col.key;
  const slug = header.trim().toLowerCase();
  if (HEADER_OVERRIDES[slug]) return HEADER_OVERRIDES[slug];
  const snake = slug.replace(/\s+/g, '_');
  return HEADER_OVERRIDES[snake] ?? snake;
}

const LIST_KEYS = new Set(['preconditions']);
const NUMERIC_KEYS = new Set([
  'elapsed_time_value',
  'expertise_value',
  'knowledge_value',
  'window_value',
  'equipment_value',
  'total_attack_potential',
]);

/** Parse a CSV file into attack-path records. List + numeric coercion. */
export function parseAttackPathCsv(text: string): AttackPathRecord[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(headerToKey);
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      if (LIST_KEYS.has(key)) {
        out[key] = raw
          .split(/[;\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
      } else if (NUMERIC_KEYS.has(key)) {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : raw;
      } else {
        out[key] = raw;
      }
    });
    return out as AttackPathRecord;
  });
}

/** Parse a JSON file. Accepts `[…]`, `{ attack_analysis: [...] }`,
 *  `{ attack_paths: [...] }`, or legacy `{ attack_path_analyses: [...] }`. */
export function parseAttackPathJson(text: string): AttackPathRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as AttackPathRecord[];
  if (parsed && Array.isArray(parsed.attack_analysis)) {
    return parsed.attack_analysis as AttackPathRecord[];
  }
  if (parsed && Array.isArray(parsed.attack_paths)) {
    return parsed.attack_paths as AttackPathRecord[];
  }
  if (parsed && Array.isArray(parsed.attack_path_analyses)) {
    return parsed.attack_path_analyses as AttackPathRecord[];
  }
  throw new Error('JSON must be an array of attack paths or `{ attack_analysis: [...] }`');
}

/** Dispatcher: CSV vs JSON by file extension; JSON-first fallback. */
export async function parseAttackPathFile(file: File): Promise<AttackPathRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseAttackPathJson(text);
  if (ext === 'csv') return parseAttackPathCsv(text);
  try {
    return parseAttackPathJson(text);
  } catch {
    return parseAttackPathCsv(text);
  }
}
